import { useCallback, useLayoutEffect, useReducer, useRef, useState } from 'react';
import useAccountActionScope from './useAccountActionScope';

// A successful canonical empty read is the only onboarding authority. Search,
// invites and roster reads cannot establish absence of membership.
export default function useDojoMembershipRead(service, enabled = true) {
  const [, changed] = useReducer(value => value + 1, 0);
  useLayoutEffect(() => service.onAccountScopeChanged?.(changed), [service]);
  const captureAccount = useAccountActionScope(service, enabled);
  const [result, setResult] = useState(null);
  const live = useRef(null);
  if (!live.current || live.current.captureAccount !== captureAccount) {
    live.current = { captureAccount, request: 0, status: 'pending', enabled };
  }
  const scope = live.current;
  useLayoutEffect(() => () => { scope.request++; scope.status = 'pending'; }, [scope]);
  const load = useCallback(async () => {
    const account = captureAccount();
    if (!enabled || !account.current() || live.current !== scope) return null;
    const request = ++scope.request;
    const current = () => account.current() && live.current === scope && scope.request === request;
    scope.status = 'pending';
    setResult({ scope, status: 'pending', dojo: null });
    try {
      const dojo = await service.getMyDojo();
      if (!current()) return null;
      // Undefined/malformed responses are not authoritative empty membership.
      if (dojo !== null && !dojo?._id) throw new Error('Invalid membership response');
      scope.status = dojo ? 'member' : 'empty';
      setResult({ scope, status: scope.status, dojo });
      return { dojo, current };
    } catch (_) {
      if (current()) {
        scope.status = 'error';
        setResult({ scope, status: 'error', dojo: null });
      }
      return null;
    }
  }, [captureAccount, enabled, scope, service]);
  const status = enabled && result?.scope === scope ? result.status : 'pending';
  const captureEmpty = useCallback(() => {
    const account = captureAccount(), request = scope.request;
    const current = () => enabled && account.current() && live.current === scope && scope.status === 'empty' && scope.request === request;
    return { current };
  }, [captureAccount, enabled, scope]);
  return { dojo: status === 'member' ? result.dojo : null, status, load, captureEmpty, captureAccount };
}
